import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductService } from '../../shared/service/product.service';
import { TokenService } from '../../shared/service/token.service';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { AudioService } from '../../shared/service/audio.service';
import {
  PoPageModule,
  PoFieldModule,
  PoButtonModule,
  PoInfoModule,
  PoWidgetModule,
  PoDialogService
} from '@po-ui/ng-components';
import { ToasterService } from '../../shared/service/toaster.service';
import { ToasterComponent } from '../../shared/components/toaster/toaster.component';

@Component({
  selector: 'app-batch-item-location',
  templateUrl: './batch-item-location.component.html',
  styleUrls: ['./batch-item-location.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PoPageModule,
    PoFieldModule,
    PoButtonModule,
    PoInfoModule,
    PoWidgetModule,
    ZXingScannerModule,
    ToasterComponent
  ]
})
export class BatchItemLocationComponent implements OnInit {
  @ViewChild('itemBarcodeInput', { static: false }) itemBarcodeInputRef!: ElementRef;

  locationCode: string = '';
  itemBarcode: string = '';
  locationData: any = null;
  locatedItems: any[] = [];
  localCode: number | null = null;

  scannerVisible = false;
  selectedDevice: MediaDeviceInfo | undefined = undefined;
  scannerTarget: 'item' | 'local' = 'item';

  formatsEnabled: BarcodeFormat[] = [
    BarcodeFormat.CODE_128,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.CODE_39,
    BarcodeFormat.ITF
  ];

  constructor(
    private productService: ProductService,
    private tokenService: TokenService,
    private poDialog: PoDialogService,
    private toasterService: ToasterService,
    private audioService: AudioService
  ) { }

  ngOnInit(): void {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      if (videoDevices.length > 0) {
        this.selectedDevice = videoDevices[1];
      }
    });
  }

  toggleScannerFor(target: 'item' | 'local'): void {
    if (this.scannerVisible && this.scannerTarget === target) {
      this.scannerVisible = false;
      return;
    }
    this.scannerTarget = target;
    this.scannerVisible = true;
  }

  parseBarcode(barcode: string) {
    if (!/^[0-9]{17}$/.test(barcode)) {
      throw new Error('Invalid barcode format: must be exactly 17 numeric digits.');
    }

    const companyCodeStr = barcode.substring(0, 4);
    const batchCodeStr = barcode.substring(4, 10);
    const itemClientCode = barcode.substring(10, 13);
    const sequenceStr = barcode.substring(13, 17);

    return {
      companyCode: parseInt(companyCodeStr, 10),
      batchCode: parseInt(batchCodeStr, 10),
      itemClientCode,
      sequence: parseInt(sequenceStr, 10)
    };
  }

  loadBatchDetails(): void {
    const locationCode = this.localCode?.toString().trim();
    if (!locationCode) {
      this.toasterService.triggerAlert('Informe o código do lote/local.', 'warning');
      return;
    }

    this.productService.searchBatchLocation(locationCode).subscribe({
      next: (res) => {
        if (res?.items?.length > 0) {
          const local = res.items[0];

          this.locationData = {
            code: `${local.locationCode} - ${local.locationDescription}`,
            sector: local.sectorCode ? `${local.sectorCode} - ${local.sectorDescription}`:null,
            description: local.locationDescription,
            sectorBalanceDescription: local.sectorBalanceDescription,
            itemMaximumQuantity: local.itemMaximumQuantity,
            maximumQuantity: local.maximumQuantity,
            sectorBalanceCode: local.sectorBalanceCode,
            weight: 0,
            maximumWeight: local.maximumWeight,
            quantity: 0,
            itemsCount: 0,
            availableQuantity: 0,
            availableWeight: local.maximumWeight,
            availableItems: 0
          };

          this.loadBatchItems(local.locationCode);
        } else {
          this.toasterService.triggerAlert('Local não encontrado.', 'warning');
          this.locationData = null;
        }
      },
      error: () => {
        this.toasterService.triggerAlert('Erro ao consultar o local.', 'error');
        this.locationData = null;
      }
    });
  }

  loadBatchItems(locationCode: string): void {
    const branchCode = Number(this.tokenService.getBranch());

    this.productService.searchBatchItem(branchCode, locationCode, undefined).subscribe({
      next: (res) => {
        const items = res?.items || [];

        this.locatedItems = items.map((item: any) => ({
          company: item.branchCode,
          batch: item.batchCode,
          item: `${item.productCode} - ${item.productName}`,
          barcode: item.itemClient,
          quantity: item.quantityItem,
          sequence: item.sequence,
          weight: item.weight || 0
        }));

        const totalQuantity = this.locatedItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
        const totalWeight = this.locatedItems.reduce((sum, i) => sum + (i.weight || 0), 0);

        this.locationData.quantity = totalQuantity;
        this.locationData.weight = totalWeight;
        this.locationData.itemsCount = this.locatedItems.length;
        this.locationData.availableQuantity = this.locationData.maximumQuantity - totalQuantity;
        this.locationData.availableItems = this.locationData.itemMaximumQuantity - this.locatedItems.length;
        this.locationData.availableWeight = this.locationData.maximumWeight - totalWeight;
      },
      error: () => {
        this.toasterService.triggerAlert('Erro ao buscar itens no local.', 'error');
        this.locatedItems = [];
      }
    });
  }
  private continueAddItem(item: any): void {
    const alreadyExists = this.locatedItems.some(i => i.barcode === item.itemClient);
    if (alreadyExists) {
      this.toasterService.triggerAlert('Este item já foi vinculado a este local.', 'warning');
      this.itemBarcode = '';
      return;
    }

    const quantityToAdd = item.quantityItem || 0;
    const weightToAdd = item.weight || 0;

    const exceedItem = this.locationData.itemsCount >= this.locationData.itemMaximumQuantity;
    const exceedQuantity = this.locationData.quantity + quantityToAdd > this.locationData.maximumQuantity;
    const exceedWeight = this.locationData.weight + weightToAdd > this.locationData.maximumWeight;

    const continueAdd = () => {
      const newItem = {
        company: item.branchCode.toString().padStart(3, '0'),
        batch: item.batchCode.toString(),
        item: item.productName,
        barcode: item.itemClient,
        quantity: item.quantityItem,
        sequence: item.sequence,
        weight: item.weight || 0
      };

      this.locatedItems.unshift(newItem);
      this.updateLocationStats();

      const payload = {
        batchItems: [
          {
            branchCode: item.branchCode,
            batchCode: item.batchCode,
            sequence: item.sequence,
            dataChanged: {
              location: String(this.localCode),
            }
          }
        ]
      };

      this.productService.updateBatchInfo(payload.batchItems).subscribe({
        next: () => {
          this.toasterService.triggerAlert('Item adicionado com sucesso.', 'success');
          this.resetBarcodeField();
        },
        error: () => {
          this.toasterService.triggerAlert('Erro ao atualizar item no lote.', 'error');
        }
      });
    };

    if (exceedItem || exceedQuantity || exceedWeight) {
      const messages = [];
      if (exceedItem) messages.push('Número de itens excedido.');
      if (exceedQuantity) messages.push('Quantidade excedida.');
      if (exceedWeight) messages.push('Peso excedido.');
      messages.push('Deseja continuar?');

      this.poDialog.confirm({
        title: 'Limites excedidos',
        message: messages.join('\n'),
        confirm: continueAdd,
        cancel: () => {
          this.toasterService.triggerAlert('Ação cancelada.', 'warning');
          this.itemBarcode = '';
        }
      });
    } else {
      continueAdd();
    }
  }
  private updateLocationStats(): void {
    const totalQuantity = this.locatedItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
    const totalWeight = this.locatedItems.reduce((sum, i) => sum + (i.weight || 0), 0);

    this.locationData.quantity = totalQuantity;
    this.locationData.weight = totalWeight;
    this.locationData.itemsCount = this.locatedItems.length;
    this.locationData.availableQuantity = this.locationData.maximumQuantity - totalQuantity;
    this.locationData.availableItems = this.locationData.itemMaximumQuantity - this.locatedItems.length;
    this.locationData.availableWeight = this.locationData.maximumWeight - totalWeight;
  }

  private resetBarcodeField(): void {
    this.itemBarcode = '';
    setTimeout(() => {
      const input = this.itemBarcodeInputRef?.nativeElement?.querySelector('input');
      if (input) input.focus();
    });
  }

  onAddItem(): void {
    if (!this.itemBarcode.trim()) return;

    try {
      const branchCode = Number(this.tokenService.getBranch());

      this.productService.searchBatchItem(branchCode, '', this.itemBarcode).subscribe({
        next: (res) => {
          const item = res?.items?.[0];

          if (!item) {
            this.toasterService.triggerAlert('Item não encontrado.', 'warning');
            return;
          }

          if (item.transactions?.length > 0) {
            this.toasterService.triggerAlert('Item já faturado. Não pode ser transferido.', 'warning');
            return;
          }

          if ( this.locationData?.sectorBalanceCode != null && this.locationData?.sectorBalanceCode != item.stockCode) {            
            this.toasterService.triggerAlert(`Saldo ${item.stockDescription} do item de lote difere do saldo ${this.locationData?.sectorBalanceDescription} atribuído ao local. Operação não permitida.`, 'warning');
            this.resetBarcodeField();
            return;
          }

          if (item.locationCode != null && item.locationCode != this.localCode) {
            this.audioService.playBeep(3);
            this.poDialog.confirm({
              title: 'Confirmar movimentação',
              message: `O item <strong>${item.itemClient}</strong> está no local <strong>${item.locationCode}</strong> e será movido para <strong>${this.localCode}</strong>.<br>Deseja continuar?`,
              confirm: () => this.continueAddItem(item),
              cancel: () => {
                this.toasterService.triggerAlert('Ação cancelada.', 'warning');
                this.resetBarcodeField();
              }
            });
            return;
          }

          if (item.balance != null && item.balance <= 0) {
            this.audioService.playBeep(3);
            this.poDialog.confirm({
              title: 'Confirmar movimentação',
              message: `Item <strong>${item.itemClient}</strong> sem quantidade pendente.<br>Deseja atribuir mesmo assim?`,
              confirm: () => this.continueAddItem(item),
              cancel: () => {
                this.toasterService.triggerAlert('Ação cancelada.', 'warning');
                this.resetBarcodeField();
              }
            });
            return;
          }

          this.continueAddItem(item);
        },
        error: () => {
          this.toasterService.triggerAlert('Erro ao consultar item.', 'error');
        }
      });
    } catch {
      this.toasterService.triggerAlert('Código de barras inválido.', 'warning');
    }
  }


  removeItem(barcode: string): void {
    const index = this.locatedItems.findIndex(i => i.barcode === barcode);
    if (index === -1) {
      this.toasterService.triggerAlert('Item não encontrado.', 'warning');
      return;
    }

    const item = this.locatedItems[index];

    const payload = {
      batchItems: [
        {
          branchCode: Number(item.company),
          batchCode: Number(item.batch),
          sequence: item.sequence,
          dataChanged: {
            location: '',
          }
        }
      ]
    };

    this.productService.updateBatchInfo(payload.batchItems).subscribe({
      next: () => {
        this.locatedItems.splice(index, 1);
        const totalQuantity = this.locatedItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
        this.locationData.quantity = totalQuantity;
        this.locationData.itemsCount = this.locatedItems.length;
        this.locationData.availableQuantity = this.locationData.maxQuantity - totalQuantity;
        this.locationData.weight = 0;
        this.locationData.availableWeight = this.locationData.maxWeight;
        this.toasterService.triggerAlert('Item desvinculado com sucesso.', 'success');
      },
      error: () => {
        this.toasterService.triggerAlert('Erro ao desvincular o item.', 'error');
      }
    });
  }

  onClear(): void {
    this.localCode = null;
    this.itemBarcode = '';
    this.locationData = null;
    this.locatedItems = [];
    this.scannerVisible = false;

    setTimeout(() => {
      const input = document.querySelector('input[name="localCode"]') as HTMLInputElement;
      if (input) input.focus();
    }, 300);
  }




  openScanner(): void {
    this.scannerVisible = true;
  }

  closeScanner(): void {
    this.scannerVisible = false;
  }

  onBarcodeScanned(code: string): void {
    this.scannerVisible = false;

    if (this.scannerTarget === 'item') {
      this.itemBarcode = code;
      this.onAddItem();
    } else {
      this.localCode = Number(code);
      this.loadBatchDetails();
    }
  }
  getAvailabilityTag(value: number): string {
    if (value > 0) {
      return 'success';
    } else if (!value || value === 0) {
      return 'warning';
    } else {
      return 'danger';
    }
  }

  confirmDeleteItem(barcode: string): void {
    this.audioService.playBeep(3);
    this.poDialog.confirm({
      title: 'Confirmar remoção',
      message: `Deseja realmente remover o item  <br><strong>${barcode}</strong> ?`,
      confirm: () => { this.removeItem(barcode) }
    });
  }

}
